import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      setIsSubmitted(true);
      toast.success('¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Error al enviar el mensaje. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Contáctanos
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Solicita Tu{' '}
            <span className="bg-gradient-to-r from-blue-600 to-blue-600 text-transparent bg-clip-text">
              Presupuesto Gratis
            </span>
          </h2>
          <p className="text-lg text-slate-600">
            Estamos listos para ayudarte con tu próximo proyecto. Contáctanos hoy mismo
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                Información de Contacto
              </h3>
              <p className="text-slate-600 mb-8">
                Contáctanos por cualquiera de estos medios y te responderemos lo antes posible
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <a
                href={`tel:${companyInfo.phone}`}
                className="group flex items-start gap-4 bg-slate-50 hover:bg-blue-50 p-6 rounded-2xl transition-all duration-300 border border-slate-200 hover:border-blue-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Teléfono</h4>
                  <p className="text-blue-600 font-semibold text-lg">
                    {companyInfo.phone}
                  </p>
                  <p className="text-slate-500 text-sm mt-1">
                    Llámanos para presupuesto inmediato
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Email</h4>
                  <p className="text-slate-600 font-medium">
                    {companyInfo.email}
                  </p>
                  <p className="text-slate-500 text-sm mt-1">
                    Envíanos un correo electrónico
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Área de Servicio</h4>
                  <p className="text-slate-600 font-medium">
                    {companyInfo.address}
                  </p>
                  <p className="text-slate-500 text-sm mt-1">
                    Servicios en toda la región
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Horario</h4>
                  <p className="text-slate-600 font-medium">
                    Lun - Vie: 7:00 AM - 6:00 PM
                  </p>
                  <p className="text-slate-600 font-medium">
                    Sábado: 8:00 AM - 4:00 PM
                  </p>
                  <p className="text-slate-500 text-sm mt-1">
                    Disponibles para emergencias 24/7
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Envíanos un Mensaje
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-slate-700 font-semibold mb-2"
                >
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-slate-700 font-semibold mb-2"
                >
                  Correo Electrónico *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-slate-700 font-semibold mb-2"
                >
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="(123) 456-7890"
                />
              </div>

              <div>
                <label
                  htmlFor="service"
                  className="block text-slate-700 font-semibold mb-2"
                >
                  Servicio de Interés *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="">Selecciona un servicio</option>
                  <option value="plumbing">Plomería</option>
                  <option value="electrical">Electricidad</option>
                  <option value="painting">Pintura</option>
                  <option value="masonry">Albañilería</option>
                  <option value="flooring">Pisos</option>
                  <option value="drywall">Reparación de Drywall</option>
                  <option value="roofing">Reparación de Techos</option>
                  <option value="molding">Molduras y Acabados</option>
                  <option value="other">Otro</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-slate-700 font-semibold mb-2"
                >
                  Mensaje *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  placeholder="Cuéntanos sobre tu proyecto..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isSubmitted}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                  isSubmitted
                    ? 'bg-green-500 text-white'
                    : 'bg-gradient-to-r from-blue-500 to-blue-500 text-white hover:shadow-xl hover:shadow-blue-500/50'
                } disabled:opacity-70 disabled:cursor-not-allowed`}
              >
                {isSubmitted ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Mensaje Enviado
                  </>
                ) : isSubmitting ? (
                  'Enviando...'
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Enviar Mensaje
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
